import { TFunction } from 'i18next'
import { FC, MouseEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { useMeetingPdfStatus } from '../../../graphql/meeting/hooks/useMeetingPdfStatus'
import { MeetingPdfStatus, MeetingDashboardFragment, MeetingStatus, MeetingType } from '../../../types'
import { TimeZoneAndLocale } from '../../../types.custom'
import { getPreparationPdfHrefByMeeting } from '../../meeting/util/hrefUtils'
import { PerActionButton } from './ActionButton'

type EmployeeMeetingActionButtonProps = {
  meeting: MeetingDashboardFragment
  timezoneAndLocale: TimeZoneAndLocale
  isLoading?: boolean
  isConfirming?: boolean
  setConfirmModalOpen?: () => void
}

export const EmployeeMeetingActionButton: FC<EmployeeMeetingActionButtonProps> = ({
  meeting,
  setConfirmModalOpen,
  isLoading = false,
  isConfirming = false,
}) => {
  const [t] = useTranslation()
  const history = useHistory()

  const { data, startPolling, stopPolling } = useMeetingPdfStatus(meeting.id)

  const shouldPollingBeEnabled = (meeting) => {
    if (meeting.status === MeetingStatus.PendingConfirmation && meeting.conclusionPdfStatus !== MeetingPdfStatus.FinishedSuccess)
      // manager closed meeting and pdf is not generated yet
      return true
    else if (
      (meeting.status === MeetingStatus.Draft || meeting.status === MeetingStatus.Prepared) &&
      meeting.employeePrepared &&
      meeting.preparationPdfStatus !== MeetingPdfStatus.FinishedSuccess
    ) {
      //employee prepared and preparation pdf is not generated yet
      return true
    } else {
      return false
    }
  }
  useEffect(() => {
    let handleTimeout: number
    if (shouldPollingBeEnabled(meeting)) {
      startPolling(5000)
      handleTimeout = window.setTimeout(() => {
        stopPolling()
      }, 30000)
    } else {
      stopPolling()
    }
    return () => window.clearTimeout(handleTimeout)
    // I'm 100% sure that startPolling() and stopPolling() do not need to be in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting.conclusionPdfStatus, meeting.status, meeting.preparationPdfStatus])

  let downloadHref: undefined | string = undefined

  const conclusionPdfLoaded =
    meeting.conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess ||
    (data && data.meetingPdfStatus.conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess)
  const preparationPdfLoaded =
    meeting.preparationPdfStatus === MeetingPdfStatus.FinishedSuccess ||
    (data ? data.meetingPdfStatus.preparationPdfStatus === MeetingPdfStatus.FinishedSuccess : false)

  downloadHref = getPreparationPdfHrefByMeeting(meeting)

  switch (meeting.status) {
    case MeetingStatus.Canceled:
      return <CancelledButton isLoading={isLoading} />

    case MeetingStatus.Draft: {
      if (!meeting.employeePrepared) {
        return (
          <PrepareButton
            meeting={meeting}
            isLoading={isLoading}
            clickHandler={(event) => {
              event.preventDefault()
              history.push('/employeePrepareMeeting/' + meeting.id)
            }}
          />
        )
      } else {
        if (meeting.preparationPdfStatus === MeetingPdfStatus.FinishedError || meeting.preparationPdfFileName === 'error.pdf') {
          return (
            <PreparedEmployeeButton
              buttonText={t('dashboard.employee.meetings.preparationPdfNotAvailable')}
              isLoading={false}
              pdfInProgress={false}
              downloadHref={downloadHref}
            />
          )
        }

        return (
          <PreparedEmployeeButton
            buttonText={t('dashboard.employee.meetings.viewPreparation')}
            isLoading={isLoading}
            pdfInProgress={!preparationPdfLoaded}
            downloadHref={downloadHref}
          />
        )
      }
    }

    case MeetingStatus.Prepared:
      if (
        meeting.preparationPdfStatus === MeetingPdfStatus.NotStarted &&
        (meeting.preparationPdfFileName === 'noPreparationSnapshot.pdf' || meeting.preparationPdfFileName === 'error.pdf')
      ) {
        return (
          <PreparedEmployeeButton
            buttonText={t('dashboard.employee.meetings.preparationPdfNotAvailable')}
            isLoading={false}
            pdfInProgress={false}
            downloadHref={downloadHref}
          />
        )
      }
      if (meeting.preparationPdfStatus === MeetingPdfStatus.NotStarted) {
        return (
          <PreparedEmployeeButton
            buttonText={t('dashboard.employee.meetings.pdfGenerationNotStarted')}
            isLoading={isLoading}
            pdfInProgress={!preparationPdfLoaded}
            downloadHref={downloadHref}
          />
        )
      }

      return (
        <PreparedEmployeeButton
          buttonText={t('dashboard.employee.meetings.viewPreparation')}
          pdfInProgress={!preparationPdfLoaded}
          isLoading={isLoading}
          downloadHref={downloadHref}
        />
      )

    case MeetingStatus.PendingConfirmation: {
      if (meeting.type === MeetingType.Appraisal || meeting.type === MeetingType.AppraisalUpdate) {
        if (meeting.conclusionPdfFileName === 'error.pdf') {
          return (
            <ConfirmButton
              clickHandler={() => setConfirmModalOpen && setConfirmModalOpen()}
              pdfInProgress={!conclusionPdfLoaded}
              isConfirming={isConfirming || isLoading}
              errorInPdf={true}
            />
          )
        }
        return (
          <ConfirmButton
            clickHandler={() => setConfirmModalOpen && setConfirmModalOpen()}
            pdfInProgress={!conclusionPdfLoaded}
            isConfirming={isConfirming || isLoading}
          />
        )
      }
      return <></>
    }

    case MeetingStatus.Confirmed:
      return <ConfirmedButton buttonText={t('dashboard.employee.meetings.confirmed')} isLoading={isLoading} />

    case MeetingStatus.DisputedConfirmed:
      return <ConfirmedButton buttonText={t('dashboard.employee.meetings.disputedConfirmed')} isLoading={isLoading} />

    default:
      return <></>
  }
}

/*
 * The components below are an exception to our rule to have only one component by file.
 * The components are not designed to be re-usable but should only be used by the main component
 * EmployeeMeetingActionButton. The components are kept here because to move them to a different file they would then
 * need to be exported.
 */

const CancelledButton: FC<{ isLoading: boolean }> = ({ isLoading = false }) => {
  const [t] = useTranslation()

  return (
    <PerActionButton
      text={t('dashboard.employee.meetings.canceled')}
      icon="close_stroke"
      isProcessing={{
        active: isLoading,
      }}
    />
  )
}

const PrepareButton: FC<{ clickHandler(event: MouseEvent): void; meeting: MeetingDashboardFragment; isLoading: boolean }> = ({
  clickHandler,
  meeting,
  isLoading,
}) => {
  const [t] = useTranslation()

  return (
    <PerActionButton
      text={t('meetingButtonActions.prep', { meetingType: t(`meetingTypes.${meeting.type}`) })}
      icon="edit_fill"
      color="warning"
      clickHandler={clickHandler}
      isProcessing={{
        active: isLoading,
      }}
      data-e2e-employee-meeting-action-button="prepare"
    />
  )
}

const isProcessingText = (t: TFunction, pdfInProgress: boolean, isConfirming = false) => {
  if (pdfInProgress) {
    return t('dashboard.employee.meetings.pdfGenerationInProgress')
  } else if (isConfirming) {
    return t('dashboard.employee.meetings.confirming')
  } else {
    return undefined
  }
}
const ConfirmButton: FC<{ clickHandler(event: MouseEvent): void; pdfInProgress: boolean; isConfirming: boolean; errorInPdf?: boolean }> = ({
  clickHandler,
  pdfInProgress,
  isConfirming,
  errorInPdf,
}) => {
  const [t] = useTranslation()
  if (!errorInPdf) {
    return (
      <PerActionButton
        text={t('dashboard.employee.meetings.viewPdfAndConfirm')}
        icon="view_fill"
        color="success"
        clickHandler={clickHandler}
        isProcessing={{
          active: pdfInProgress || isConfirming,
          text: isProcessingText(t, pdfInProgress, isConfirming),
        }}
        data-e2e-employee-meeting-action-button="reschedule"
      />
    )
  }
  return (
    <PerActionButton
      text={t('dashboard.employee.meetings.pdfGenerationError')}
      icon="viewNo_fill"
      color="error"
      data-e2e-employee-meeting-action-button="reschedule"
    />
  )
}

const ConfirmedButton: FC<{ buttonText: string; isLoading: boolean }> = ({ buttonText, isLoading = false }) => {
  return (
    <PerActionButton
      text={buttonText}
      icon="check_stroke"
      isProcessing={{
        active: isLoading,
      }}
    />
  )
}
const PreparedEmployeeButton: FC<{
  buttonText: string
  pdfInProgress: boolean
  isLoading: boolean
  downloadHref: string | undefined
}> = ({ buttonText, pdfInProgress, isLoading, downloadHref }) => {
  const [t] = useTranslation()
  if (!pdfInProgress && !isLoading && (!downloadHref || downloadHref === 'noPreparationSnapshot.pdf' || downloadHref === 'error.pdf')) {
    return (
      <PerActionButton
        text={buttonText}
        disabled={true}
        color="info"
        icon="view_fill"
        data-e2e-employee-meeting-action-button="viewPreparation"
      />
    )
  }
  return (
    <PerActionButton
      text={buttonText}
      disabled={false}
      color="info"
      isProcessing={{
        active: pdfInProgress || isLoading,
        text: isProcessingText(t, pdfInProgress),
      }}
      icon="view_fill"
      asLink={downloadHref ? downloadHref : '#'}
      data-e2e-employee-meeting-action-button="viewPreparation"
    />
  )
}
